import { z } from "zod";

export enum TranslationStatusValue {
  NA = "NON_AVAILABLE",
  MachineTranslated = "MACHINE_TRANSLATED",
  UpdatedInUserDict = "UPDATED_IN_USER_DICT",
  IntegratedDictionary = "INTEGRATED_DICTIONARY",
}

// Part of the admin panel where the translation status is used
export enum TranslationAction {
  ChatbotTemplate = "CHATBOT_TEMPLATE",
  ChatbotVariable = "CHATBOT_VARIABLE",
  ChatbotQuickReply = "CHATBOT_QUICK_REPLY",
  FacilityType = "FACILITY_TYPE",
  NA = "NON_AVAILABLE",
}

export type AdminDictType = "user_dict" | "shared_dict" | "all";

export type TranslationStatus = z.infer<typeof TranslationStatusSchema>;
export const TranslationStatusSchema = z.object({
  // TranslationAction,
  status: z.nativeEnum(TranslationStatusValue),
  updated_dict_lang_cds: z.array(z.string()),
  is_all_lang_locked: z.boolean(),
});

export type Dict = z.infer<typeof DictSchema>;
export const DictSchema = z.object({
  id: z.number().optional(),
  dict_cd: z.string().optional(),
  lang_cd: z.string().optional(),
  message: z.string().nullable().optional(),
});

export type DictStatus = z.infer<typeof DictStatusSchema>;
export const DictStatusSchema = z.object({
  contents: z.string().nullable(),
  dict_cd: z.string(),
  translation_status: TranslationStatusSchema.optional(),
});

export type SystemMessageDictStatus = z.infer<typeof SystemMessageDictStatusSchema>;
export const SystemMessageDictStatusSchema = z.object({
  status: z.string(),
  updated_dict_lang_cds: z.array(z.string()),
});

// Translation alert props object
export type TranslationAlert = z.infer<typeof TranslationAlertSchema>;
export const TranslationAlertSchema = z.object({
  alertMessage1: z.string(),
  alertMessage2: z.string(),
  alertOkLabel: z.string(),
  alertCancelLabel: z.string(),
});

export type AdminDictionary = z.infer<typeof AdminDictionarySchema>;
export const AdminDictionarySchema = z.object({
  dict_cd: z.string(),
  lang_cd: z.string(),
  //  AdminDictType
  dict_type: z.enum(["user_dict", "shared_dict", "all"]),
  message: z.string(),
});

export type AdminDictionaries = z.infer<typeof AdminDictionariesSchema>;
export const AdminDictionariesSchema = z.object({
  count: z.number(),
  dictionaries: z.array(AdminDictionarySchema),
});

export type DictTranslationStatus = z.infer<typeof DictTranslationStatusSchema>;
export const DictTranslationStatusSchema = z.object({
  contents: z.string().optional(),
  dict_cd: z.string().optional(),
  translation_status: TranslationStatusSchema.optional(),
});

export type QcBizDict = z.infer<typeof QcBizDictSchema>;
export const QcBizDictSchema = z.object({
  id: z.number(),
  tenant_id: z.number(),
  dict_cd: z.string(),
  lang_cd: z.string(),
  message: z.string(),
});

export type DictMessage = z.infer<typeof DictMessageSchema>;
export const DictMessageSchema = z.object({
  langCd: z.string(),
  message: z
    .string()
    .nullable()
    .transform((value) => value ?? ""),
  isAllowedToUpdate: z.boolean(),
});

export type Dictionary = z.infer<typeof DictionarySchema>;
export const DictionarySchema = z.object({
  dictCd: z.string(),
  messages: z.array(DictMessageSchema),
  selected: z.boolean().optional(),
});

export type DictionaryList = z.infer<typeof DictionaryListSchema>;
export const DictionaryListSchema = z.object({
  tenantId: z.number().default(0),
  page: z.number().default(0),
  totalPage: z.number().default(0),
  items: z.array(DictionarySchema),
});

export type SubMenuDictRelationItem = z.infer<typeof SubMenuDictRelationItemSchema>;
export const SubMenuDictRelationItemSchema = z.object({
  sub_menu_id: z.union([z.number(), z.string()]),
  menu_type: z.string(),
  text: z.string().optional(),
  name: z.string(),
});

export type SubMenuDictRelation = z.infer<typeof SubMenuDictRelationSchema>;
export const SubMenuDictRelationSchema = z.object({
  sub_menu_relations: z.array(SubMenuDictRelationItemSchema),
});

export type DictItem = z.infer<typeof DictItemSchema>;
export const DictItemSchema = z.object({
  dictCd: z.string(),
  messages: z.array(DictMessageSchema),
  autofillCn: z.string(),
  autofillOthers: z.string(),
  selected: z.boolean().optional(),
});

export type SavingQueueResult = z.infer<typeof SavingQueueResultSchema>;
export const SavingQueueResultSchema = z.object({
  id: z.string(),
  res: z.string(),
  content: z.string(),
  failLangCds: z.array(z.string()),
  type: z.string(),
  dict: DictItemSchema,
});

export type SavingQueue = z.infer<typeof SavingQueueSchema>;
export const SavingQueueSchema = z.object({
  type: z.string(),
  dictCd: z.string(),
});

export type ExportDictStatus = z.infer<typeof ExportDictStatusSchema>;
export const ExportDictStatusSchema = z.object({
  download_url: z.string().optional(),
  message: z.string().optional(),
  status: z.string().optional(),
  unique_id: z.string().optional(),
});

export type ImportProcessResult = z.infer<typeof ImportProcessResultSchema>;
export const ImportProcessResultSchema = z.object({
  import_process: z.boolean().optional(),
  message: z.string().optional(),
  status: z.boolean().optional(),
});

export type ImportSheetFields = z.infer<typeof ImportSheetFieldsSchema>;
export const ImportSheetFieldsSchema = z.object({
  AWSAccessKeyId: z.string().optional(),
  key: z.string().optional(),
  policy: z.string().optional(),
  signature: z.string().optional(),
  xAmzSecurityToken: z.string().optional(),
});

export type ImportSheet = z.infer<typeof ImportSheetSchema>;
export const ImportSheetSchema = z.object({
  fields: ImportSheetFieldsSchema.optional(),
  url: z.string().optional(),
});

export type ImportSheetResult = z.infer<typeof ImportSheetResultSchema>;
export const ImportSheetResultSchema = z.object({
  data: ImportSheetSchema.optional(),
  message: z.string().optional(),
  success: z.boolean().optional(),
});

export type ProcessImportResult = z.infer<typeof ProcessImportResultSchema>;
export const ProcessImportResultSchema = z.object({
  error_list: z.array(z.string()).optional(),
  message: z.string().optional(),
  success: z.boolean().optional(),
});

export type ExportDictResult = z.infer<typeof ExportDictResultSchema>;
export const ExportDictResultSchema = z.object({
  status: z.string().optional(),
  unique_id: z.string().optional(),
});

export type TranslationStatusCamel = z.infer<typeof TranslationStatusCamelSchema>;
export const TranslationStatusCamelSchema = z.object({
  isAllLangLocked: z.boolean().optional(),
  status: z.string().optional(),
  updatedDictLangCds: z.array(z.string()).optional(),
});

export type DictTranslationStatusCamel = z.infer<typeof DictTranslationStatusCamelSchema>;
export const DictTranslationStatusCamelSchema = z.object({
  contents: z.string().optional(),
  dictCd: z.string().optional(),
  translationStatus: TranslationStatusCamelSchema.optional(),
});
